// Les écrans sans session : se connecter, accepter une invitation, choisir un nouveau mot de passe.
// Les liens d'invitation et de réinitialisation arrivent par SMS ou messagerie (pas d'e-mail au début) ;
// leur code est dans le fragment de l'adresse et part dans le corps de la requête.

import { type FormEvent, useEffect, useState } from 'react';

import type { InfosInvitation } from '@alfred/shared';
import { Fenetre } from '../components';
import { api } from '../lib/api';
import { TexteNotice, useNotice } from './Notice';

function Marque() {
  return (
    <div className="entete">
      <div className="marque"><span>Alfred</span></div>
    </div>
  );
}

function Champ({ libelle, ...props }: { libelle: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="champ-compte">
      <span className="muted">{libelle}</span>
      <input className="saisie texte" {...props} />
    </label>
  );
}

export function Connexion({ apres }: { apres: () => void }) {
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const envoyer = async (e: FormEvent) => {
    e.preventDefault();
    setEnvoi(true);
    try {
      await api.connexion(email, motDePasse);
      apres();
    } catch (x) {
      setErreur((x as Error).message);
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <main className="ecran">
      <Marque />
      <Fenetre>
        <form className="formulaire" onSubmit={(e) => void envoyer(e)}>
          <Champ libelle="Adresse e-mail" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <Champ libelle="Mot de passe" type="password" autoComplete="current-password" required value={motDePasse} onChange={(e) => setMotDePasse(e.target.value)} />
          {erreur && <p className="bandeau-erreur" style={{ margin: 0 }}>{erreur}</p>}
          <button className="btn principal large" disabled={envoi}>Se connecter</button>
        </form>
      </Fenetre>
      <p className="muted" style={{ fontSize: 13 }}>Mot de passe oublié ? Demandez un lien de réinitialisation à la personne qui vous a invité.</p>
    </main>
  );
}

export function Invitation({ code, apres }: { code: string; apres: () => void }) {
  const [infos, setInfos] = useState<InfosInvitation | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [champs, setChamps] = useState({ prenom: '', email: '', motDePasse: '', nomFoyer: '' });
  const [envoi, setEnvoi] = useState(false);
  const notice = useNotice();
  const [accepte, setAccepte] = useState(false);

  useEffect(() => {
    api.invitation(code).then((i) => {
      setInfos(i);
      if (i.email) setChamps((c) => ({ ...c, email: i.email ?? '' }));
    }).catch((e: Error) => setErreur(e.message));
  }, [code]);

  const envoyer = async (e: FormEvent) => {
    e.preventDefault();
    setEnvoi(true);
    try {
      await api.accepter({ code, prenom: champs.prenom, email: champs.email, motDePasse: champs.motDePasse, ...(champs.nomFoyer ? { nomFoyer: champs.nomFoyer } : {}), ...(notice?.requise && accepte ? { consentement: notice.version } : {}) });
      apres();
    } catch (x) {
      setErreur((x as Error).message);
    } finally {
      setEnvoi(false);
    }
  };
  const maj = (k: keyof typeof champs) => (e: React.ChangeEvent<HTMLInputElement>) => setChamps({ ...champs, [k]: e.target.value });

  return (
    <main className="ecran">
      <Marque />
      {!infos && !erreur && <p className="message muted">Vérification du lien…</p>}
      {erreur && !infos && <p className="bandeau-erreur">{erreur}</p>}
      {infos && (
        <Fenetre>
          <p className="message" style={{ margin: 0 }}>
            {infos.nouveauFoyer ? 'Bienvenue. Créez votre compte : votre foyer sera créé avec lui.' : <>Vous êtes invité à rejoindre <strong>{infos.foyer}</strong>.</>}
          </p>
          <form className="formulaire" onSubmit={(e) => void envoyer(e)}>
            <Champ libelle="Votre prénom" autoComplete="given-name" required value={champs.prenom} onChange={maj('prenom')} />
            <Champ libelle="Adresse e-mail" type="email" autoComplete="username" required readOnly={!!infos.email} value={champs.email} onChange={maj('email')} />
            <Champ libelle="Mot de passe (10 caractères au moins)" type="password" autoComplete="new-password" minLength={10} required value={champs.motDePasse} onChange={maj('motDePasse')} />
            {infos.nouveauFoyer && <Champ libelle="Nom du foyer (facultatif)" placeholder="La maison" value={champs.nomFoyer} onChange={maj('nomFoyer')} />}
            {notice?.requise && (
              <>
                <details>
                  <summary>Comment Alfred s’occupe de vos données (à lire)</summary>
                  <TexteNotice notice={notice} />
                </details>
                <label className="entretien-case">
                  <input type="checkbox" checked={accepte} onChange={(e) => setAccepte(e.target.checked)} />
                  <span>J’ai lu cette notice et je l’accepte.</span>
                </label>
              </>
            )}
            {erreur && <p className="bandeau-erreur" style={{ margin: 0 }}>{erreur}</p>}
            <button className="btn principal large" disabled={envoi || (notice?.requise === true && !accepte)}>Créer mon compte</button>
          </form>
          <span className="muted mono" style={{ fontSize: 11 }}>Lien valable jusqu’au {new Date(infos.expireLe).toLocaleDateString('fr-FR')}.</span>
        </Fenetre>
      )}
    </main>
  );
}

export function Reinitialisation({ code }: { code: string }) {
  const [motDePasse, setMotDePasse] = useState('');
  const [etat, setEtat] = useState<'saisie' | 'fait' | string>('saisie');

  const envoyer = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.reinitialiser(code, motDePasse);
      setEtat('fait');
    } catch (x) {
      setEtat((x as Error).message);
    }
  };

  return (
    <main className="ecran">
      <Marque />
      <Fenetre>
        {etat === 'fait' ? (
          <>
            <p className="message" style={{ margin: 0 }}>Mot de passe changé. Vos autres appareils ont été déconnectés.</p>
            <a className="btn principal large" href="#/connexion">Se connecter</a>
          </>
        ) : (
          <form className="formulaire" onSubmit={(e) => void envoyer(e)}>
            <Champ libelle="Nouveau mot de passe (10 caractères au moins)" type="password" autoComplete="new-password" minLength={10} required value={motDePasse} onChange={(e) => setMotDePasse(e.target.value)} />
            {etat !== 'saisie' && <p className="bandeau-erreur" style={{ margin: 0 }}>{etat}</p>}
            <button className="btn principal large">Enregistrer</button>
          </form>
        )}
      </Fenetre>
    </main>
  );
}
